(() => {
  if (customElements.get('zoom-dialog')) return;

  class ZoomDialog extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.maxScale = 5;
      this.swipeOffset = 0;
      this.pointers = new Map();
      this.highResImagesLoaded = new Set();
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.gallery = this.querySelector('.dialog-zoomed-gallery');
      this.media = Array.from(this.gallery?.children || []);
      this.thumbnails = this.querySelector('.dialog-thumbnails-list');
      if (!this.dialog || !this.media.length) return;
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.classList.add('hx-product-zoom');
      this.classList.toggle('hx-zoom-single', this.media.length < 2);
      this.dialog.addEventListener('click', this.onClick, options);
      this.dialog.addEventListener('cancel', this.onCancel, options);
      this.dialog.addEventListener('close', this.onClosed, options);
      this.dialog.addEventListener('keydown', this.onKeydown, options);
      this.gallery.addEventListener('pointerdown', this.onPointerDown, options);
      this.gallery.addEventListener('pointermove', this.onPointerMove, options);
      this.gallery.addEventListener('pointerup', this.onPointerEnd, options);
      this.gallery.addEventListener('pointercancel', this.onPointerCancel, options);
      this.gallery.addEventListener('lostpointercapture', this.onPointerCancel, options);
      this.gallery.addEventListener('dblclick', this.onDoubleClick, options);
      this.gallery.addEventListener('dragstart', event => event.preventDefault(), options);
      this.gallery.addEventListener('load', this.onImageLoad, { ...options, capture: true });
      this.resizeObserver = new ResizeObserver(() => {
        if (this.dialog.open) {
          this.endGesture();
          this.renderTransform();
        }
      });
      this.resizeObserver.observe(this.gallery);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.closeDialog();
    }

    open(index = 0, event) {
      event?.preventDefault();
      if (!this.dialog || !this.media.length) return;
      if (!this.dialog.open) {
        this.opener = event?.target instanceof Element
          ? event.target.closest('[data-zoom-target]') || event.target.closest('button, a, [tabindex]')
          : document.activeElement;
        this.dialog.showModal();
      }
      this.selectThumbnail(index);
      this.dialog.querySelector('.dialog-zoomed-gallery__close-button')?.focus({ preventScroll: true });
    }

    closeDialog() {
      if (!this.dialog?.open) return;
      this.endGesture();
      this.pauseMedia();
      this.dialog.close();
      window.dispatchEvent(new CustomEvent('dialog-close'));
    }

    close() { this.closeDialog(); }

    onClosed = () => {
      this.endGesture();
      this.reset();
      const opener = this.opener;
      this.opener = null;
      requestAnimationFrame(() => {
        if (!opener?.isConnected) return;
        const isVisible = element => !element.disabled && !element.closest('[inert]') && element.getClientRects().length > 0 && element.offsetWidth > 0 && element.offsetHeight > 0 && getComputedStyle(element).visibility !== 'hidden';
        // A theme zoom button may exist in the DOM while being hidden at this breakpoint.
        const target = [opener, ...opener.querySelectorAll('button, a, [tabindex]')]
          .find(element => element.matches('button, a[href], [tabindex]') && isVisible(element));
        if (target) {
          target.focus({ preventScroll: true });
          if (document.activeElement === target) return;
        }
        const container = opener.closest('[data-zoom-target]') || opener;
        const previousTabindex = container.getAttribute('tabindex');
        container.setAttribute('tabindex', '-1');
        container.focus({ preventScroll: true });
        const restoreTabindex = () => {
          if (previousTabindex === null) container.removeAttribute('tabindex');
          else container.setAttribute('tabindex', previousTabindex);
        };
        if (document.activeElement === container) container.addEventListener('blur', restoreTabindex, { once: true });
        else restoreTabindex();
      });
    };

    onCancel = event => {
      event.preventDefault();
      this.closeDialog();
    };

    onClick = event => {
      const action = event.target.closest('[data-zoom-action]')?.dataset.zoomAction;
      if (!action) return;
      event.preventDefault();
      if (action === 'previous') this.selectThumbnail(this.activeIndex - 1);
      if (action === 'next') this.selectThumbnail(this.activeIndex + 1);
      if (action === 'in') this.zoomTo(this.scale + 0.5);
      if (action === 'out') this.zoomTo(this.scale - 0.5);
      if (action === 'reset') this.reset();
    };

    onKeydown = event => {
      if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      const actions = {
        ArrowLeft: () => this.selectThumbnail(this.activeIndex - 1),
        ArrowRight: () => this.selectThumbnail(this.activeIndex + 1),
        '+': () => this.zoomTo(this.scale + 0.5),
        '=': () => this.zoomTo(this.scale + 0.5),
        '-': () => this.zoomTo(this.scale - 0.5),
        '0': () => this.reset(),
      };
      if (!actions[event.key]) return;
      // Do not take arrow keys away from embedded video / 3D controls.
      if (!this.activeImage && event.target.closest('deferred-media, product-model')) return;
      event.preventDefault();
      actions[event.key]();
    };

    get activeMedia() { return this.media[this.activeIndex]; }
    get activeImage() { return this.activeMedia?.classList.contains('product-media-container--image') ? this.activeMedia.querySelector('img.product-media__image') : null; }

    handleThumbnailClick(index) { this.selectThumbnail(index); }

    selectThumbnail(index) {
      index = Number(index);
      if (!Number.isInteger(index) || !this.media[index]) return;
      this.endGesture();
      this.pauseMedia();
      this.activeIndex = index;
      this.media.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.toggleAttribute('data-zoom-active', active);
        item.setAttribute('aria-hidden', String(!active));
        item.toggleAttribute('inert', !active);
      });
      this.thumbnails?.querySelectorAll('[data-zoom-index]').forEach(button => {
        const active = Number(button.dataset.zoomIndex) === index;
        button.setAttribute('aria-selected', String(active));
        button.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          // Only scroll the thumbnail rail, never the underlying product page.
          const left = button.offsetLeft - this.thumbnails.clientWidth / 2 + button.offsetWidth / 2;
          this.thumbnails.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
        }
      });
      this.reset();
      this.loadHighResolutionImage(this.activeMedia);
      this.querySelector('[data-zoom-counter]').textContent = `${index + 1} / ${this.media.length}`;
      this.querySelector('[data-zoom-action="previous"]').disabled = index === 0;
      this.querySelector('[data-zoom-action="next"]').disabled = index === this.media.length - 1;
      this.querySelector('.hx-zoom-tools').hidden = !this.activeImage;
      this.dispatchEvent(new CustomEvent('zoom-media-selected', { detail: { index } }));
      this.dispatchEvent(new CustomEvent('zoom-media:selected', { detail: { index } }));
    }

    pauseMedia() {
      this.media?.forEach(item => {
        item.querySelectorAll('video').forEach(video => video.pause());
        item.querySelectorAll('iframe').forEach(frame => {
          // Known embedded media protocols only; no wildcard-origin messages.
          try {
            const url = new URL(frame.src);
            if (url.hostname === 'www.youtube.com' || url.hostname === 'www.youtube-nocookie.com') {
              frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), url.origin);
            } else if (url.hostname === 'player.vimeo.com') {
              frame.contentWindow?.postMessage({ method: 'pause' }, url.origin);
            }
          } catch (_) { /* An unloaded media frame has nothing to pause. */ }
        });
      });
    }

    loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer?.classList.contains('product-media-container--image')) return;
      const image = mediaContainer.querySelector('img.product-media__image');
      const source = image?.getAttribute('data_max_resolution');
      if (!source || this.highResImagesLoaded.has(source)) return;
      this.highResImagesLoaded.add(source);
      const highRes = new Image();
      highRes.onload = () => {
        if (!image.isConnected || image.getAttribute('data_max_resolution') !== source) return;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = source;
        if (this.activeMedia === mediaContainer) this.renderTransform();
      };
      highRes.onerror = () => this.highResImagesLoaded.delete(source);
      highRes.src = source;
    }

    onImageLoad = event => {
      if (event.target === this.activeImage) this.renderTransform();
    };

    point(clientX, clientY) {
      const rect = this.gallery.getBoundingClientRect();
      return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
    }

    zoomTo(nextScale, point = { x: 0, y: 0 }) {
      if (!this.activeImage) return;
      this.endGesture();
      const scale = Math.min(this.maxScale, Math.max(1, nextScale));
      const ratio = scale / this.scale;
      this.x = point.x - (point.x - this.x) * ratio;
      this.y = point.y - (point.y - this.y) * ratio;
      this.scale = scale;
      this.renderTransform();
    }

    reset() {
      this.endGesture();
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.lastTap = null;
      this.renderTransform();
    }

    renderTransform() {
      const image = this.activeImage;
      if (!image) return;
      const width = this.gallery.clientWidth;
      const height = this.gallery.clientHeight;
      // Clamp against the actual contained image, excluding empty letterboxing.
      const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || width;
      const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || height;
      const fit = Math.min(width / naturalWidth, height / naturalHeight);
      const maxX = Math.max(0, (naturalWidth * fit * this.scale - width) / 2);
      const maxY = Math.max(0, (naturalHeight * fit * this.scale - height) / 2);
      this.x = Math.max(-maxX, Math.min(maxX, this.x));
      this.y = Math.max(-maxY, Math.min(maxY, this.y));
      if (this.scale === 1) this.x = this.y = 0;
      image.style.setProperty('--hx-zoom-scale', this.scale);
      image.style.setProperty('--hx-zoom-x', `${this.scale === 1 ? this.swipeOffset : this.x}px`);
      image.style.setProperty('--hx-zoom-y', `${this.y}px`);
      this.activeMedia.toggleAttribute('data-zoom-magnified', this.scale > 1);
      const percent = this.querySelector('[data-zoom-percent]');
      if (percent) percent.textContent = `${Math.round(this.scale * 100)}%`;
      const zoomOut = this.querySelector('[data-zoom-action="out"]');
      const zoomIn = this.querySelector('[data-zoom-action="in"]');
      if (zoomOut) zoomOut.disabled = this.scale <= 1;
      if (zoomIn) zoomIn.disabled = this.scale >= this.maxScale;
    }

    onPointerDown = event => {
      if (!this.activeImage || !this.activeMedia.contains(event.target) || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (this.pointers.size >= 2) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.gallery.setPointerCapture(event.pointerId);
      this.activeMedia.setAttribute('data-zoom-dragging', '');
      if (this.pointers.size === 1) {
        this.gestureMoved = false;
        this.hadMultiplePointers = false;
        this.swipeOffset = 0;
        this.tapStart = { x: event.clientX, y: event.clientY, time: performance.now() };
      } else {
        this.gestureMoved = true;
        this.hadMultiplePointers = true;
        this.swipeOffset = 0;
        this.lastTap = null;
      }
      this.beginGesture();
    };

    beginGesture() {
      const points = Array.from(this.pointers.values());
      this.gesture = { x: this.x, y: this.y, scale: this.scale, points };
      if (points.length === 2) {
        this.swipeOffset = 0;
        const midpoint = this.point((points[0].x + points[1].x) / 2, (points[0].y + points[1].y) / 2);
        this.gesture.distance = Math.max(1, Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y));
        this.gesture.anchor = { x: (midpoint.x - this.x) / this.scale, y: (midpoint.y - this.y) / this.scale };
      }
    }

    onPointerMove = event => {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const points = Array.from(this.pointers.values());
      if (points.length === 2) {
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const midpoint = this.point((points[0].x + points[1].x) / 2, (points[0].y + points[1].y) / 2);
        this.scale = Math.max(1, Math.min(this.maxScale, this.gesture.scale * distance / this.gesture.distance));
        this.x = midpoint.x - this.gesture.anchor.x * this.scale;
        this.y = midpoint.y - this.gesture.anchor.y * this.scale;
      } else if (points.length === 1) {
        const deltaX = points[0].x - this.gesture.points[0].x;
        const deltaY = points[0].y - this.gesture.points[0].y;
        if (Math.hypot(deltaX, deltaY) > 6) this.gestureMoved = true;
        if (this.scale > 1) {
          this.x = this.gesture.x + deltaX;
          this.y = this.gesture.y + deltaY;
        } else if (!this.hadMultiplePointers && Math.abs(deltaX) > Math.abs(deltaY) * 1.4 && Math.abs(deltaX) > 8) {
          // At full view a horizontal gesture changes image; zoomed gestures only pan.
          const limit = this.gallery.clientWidth * 0.35;
          this.swipeOffset = Math.max(-limit, Math.min(limit, deltaX * 0.6));
        } else {
          this.swipeOffset = 0;
        }
      }
      this.renderTransform();
    };

    onPointerEnd = event => {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const wasSingle = this.pointers.size === 1;
      const deltaX = this.tapStart ? event.clientX - this.tapStart.x : 0;
      const deltaY = this.tapStart ? event.clientY - this.tapStart.y : 0;
      const threshold = Math.max(40, Math.min(72, this.gallery.clientWidth * 0.12));
      const isSwipe = wasSingle && !this.hadMultiplePointers && this.scale === 1 &&
        this.gesture?.scale === 1 && Math.abs(deltaX) >= threshold && Math.abs(deltaX) > Math.abs(deltaY) * 1.4;
      this.pointers.delete(event.pointerId);
      if (this.gallery.hasPointerCapture(event.pointerId)) this.gallery.releasePointerCapture(event.pointerId);
      if (this.pointers.size) this.beginGesture();
      else this.activeMedia?.removeAttribute('data-zoom-dragging');
      this.swipeOffset = 0;
      if (isSwipe) {
        this.lastTap = null;
        const index = this.activeIndex + (deltaX < 0 ? 1 : -1);
        if (this.media[index]) this.selectThumbnail(index);
        else this.renderTransform();
        return;
      }
      this.renderTransform();
      if (event.pointerType !== 'touch' || !wasSingle || this.gestureMoved || !this.tapStart) return;
      const now = performance.now();
      if (now - this.tapStart.time > 300) return;
      if (this.lastTap && now - this.lastTap.time < 320 && Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 28) {
        this.toggleZoom(this.point(event.clientX, event.clientY));
        this.lastTap = null;
        this.lastTouchZoom = now;
      } else this.lastTap = { x: event.clientX, y: event.clientY, time: now };
    };

    onPointerCancel = event => {
      if (!this.pointers.delete(event.pointerId)) return;
      this.lastTap = null;
      this.swipeOffset = 0;
      if (this.pointers.size) this.beginGesture();
      else this.activeMedia?.removeAttribute('data-zoom-dragging');
      this.renderTransform();
    };

    onDoubleClick = event => {
      if (!this.activeImage || !this.activeMedia.contains(event.target)) return;
      event.preventDefault();
      // Touch double taps were already handled without waiting for a click delay.
      if (performance.now() - (this.lastTouchZoom || -1000) < 500) return;
      this.toggleZoom(this.point(event.clientX, event.clientY));
    };

    toggleZoom(point) { this.zoomTo(this.scale > 1 ? 1 : 2.5, point); }

    endGesture() {
      const ids = Array.from(this.pointers.keys());
      this.pointers.clear();
      ids.forEach(id => {
        if (this.gallery?.hasPointerCapture(id)) this.gallery.releasePointerCapture(id);
      });
      this.media?.forEach(item => item.removeAttribute('data-zoom-dragging'));
      this.lastTap = null;
      this.gesture = null;
      this.swipeOffset = 0;
      this.hadMultiplePointers = false;
    }
  }

  customElements.define('zoom-dialog', ZoomDialog);

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-zoom-target]');
    if (!trigger) return;
    const target = trigger.dataset.zoomTarget;
    if (!target?.startsWith('#')) return;
    const viewer = document.getElementById(target.slice(1));
    if (!(viewer instanceof ZoomDialog)) return;
    const index = Number(trigger.dataset.zoomIndex);
    if (trigger.classList.contains('dialog-thumbnails-list__thumbnail')) {
      event.preventDefault();
      viewer.handleThumbnailClick(index);
    } else if (trigger.classList.contains('product-media-container--zoomable')) viewer.open(index, event);
  });

  // Retain the shared custom-element hook used by existing theme markup.
  if (!customElements.get('scroll-hint')) customElements.define('scroll-hint', class extends HTMLElement {});
})();
